import { cn } from 'util/cn'

import { format } from 'date-fns'
import { css } from 'design'
import { Button, buttonStyles } from 'primitives/button'
import { Stack } from 'primitives/containers'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useParticipantManager } from '../../contexts/participant_manager_context'
import { AppErrorMessage } from '../../controls/errors/app_error_message'
import Stopwatch from '../../controls/stopwatch'
import { useMediaControls } from '../../hooks/media/use_media_controls'
import { useMediaRecorder } from '../../hooks/media/use_media_recorder'
import { useTrixEditor } from '../../hooks/trix/use_trix_editor'
import { useSetError } from '../../hooks/use_set_error'
import { MicrophoneOn, VideoOnIcon } from '../../images'
import { DeviceSelectors } from '../calls/controls/device_selectors'
import { AudioIndicatorBar } from '../calls/tracks/audio_indicator_bar'
import { DevicePermissionsPrompt } from './device_permissions_prompt'

type Props = {
  mediaStreamConstraints?: MediaStreamConstraints
  backButton?: HTMLButtonElement
  SubmitButton?: any
  onSubmit: any
  cssAspectRatio?: string
  timer?: number
} & React.HTMLAttributes<HTMLDivElement>

export const MediaRecorder = ({
  mediaStreamConstraints = {},
  onSubmit,
  backButton,
  SubmitButton,
  cssAspectRatio = '9 / 16',
  timer,
  ...props
}: Props) => {
  const mediaRef = useRef<HTMLVideoElement>(null)
  const { localTrackData } = useParticipantManager()
  const { videoDeviceId, audioDeviceId } = localTrackData || {}
  const [isSubmitting, setIsSubmitting] = useState(false)
  const setError = useSetError()
  const [showSpinner, setShowSpinner] = useState(true)

  const {
    isSubmitDisabled,
    clearEditorContent,
    editor,
    handleSubmit,
  } = useTrixEditor({
    id: 'media-recorder-editor',
    onSubmit,
    persistToLS: false,
  })

  const onStop = useCallback(
    (blobUrl, blob) => {
      const timeFormat = "MMMdy_hh:mmaaaaa'm'"
      const now = Number(Date.now().toFixed(0))
      blob.lastModifiedDate = now
      blob.name = `${
        mediaStreamConstraints.video ? 'Video' : 'Audio'
      }_Message_${format(now, timeFormat)}`
      editor.insertFile(blob)
    },
    [editor, mediaStreamConstraints.video],
  )

  const constraints = useMemo(() => {
    return {
      ...mediaStreamConstraints,
      ...(mediaStreamConstraints.video && {
        video: {
          ...(videoDeviceId && { deviceId: videoDeviceId }),
          ...(mediaStreamConstraints.video !== true &&
            mediaStreamConstraints.video),
        },
      }),
      ...(mediaStreamConstraints.audio && {
        audio: {
          ...(audioDeviceId && { deviceId: audioDeviceId }),
          ...(mediaStreamConstraints.audio !== true &&
            mediaStreamConstraints.audio),
        },
      }),
    }
  }, [audioDeviceId, mediaStreamConstraints, videoDeviceId])

  const {
    startPreview,
    toggleRecording,
    recording,
    previewVideoStream,
    mediaBlobUrl,
    reset,
    audioTrack,
  } = useMediaRecorder({
    mediaStreamConstraints: constraints,
    onStop,
    timer,
  })

  const { mediaState, toggleMediaPlay } = useMediaControls({
    mediaRef,
    src: mediaBlobUrl,
  })

  const unmount = useRef(false)
  useEffect(() => {
    return () => {
      unmount.current = true
    }
  }, [])
  useEffect(() => {
    return () => {
      if (unmount.current) {
        reset()
      }
    }
  }, [reset])

  useEffect(() => {
    startPreview()
  }, [startPreview])

  useLayoutEffect(() => {
    const mediaElement = mediaRef.current
    if (!mediaElement) {
      return
    }

    const onPlaying = () => {
      setShowSpinner(false)
    }

    if (mediaBlobUrl) {
      mediaElement.srcObject = null
      mediaElement.src = mediaBlobUrl
    } else if (previewVideoStream) {
      setShowSpinner(true)
      mediaElement.src = ''
      mediaElement.addEventListener('playing', onPlaying)
      mediaElement.srcObject = previewVideoStream

      return () => {
        mediaElement.removeEventListener('playing', onPlaying)
      }
    } else {
      mediaElement.src = ''
      mediaElement.srcObject = null
    }
  }, [mediaBlobUrl, previewVideoStream, mediaRef])

  const submit = () => {
    setIsSubmitting(true)
    handleSubmit().finally(() => {
      setIsSubmitting(false)
    })
  }

  const clear = () => {
    reset()
    clearEditorContent()
  }

  const toggleRecordingWrapper = () => {
    toggleRecording().catch((e) => {
      setError({ domId: RECORD_BTN_ID, error: e.message })
    })
  }

  return (
    <DevicePermissionsPrompt
      className={permissionPromptStyles()}
      video={!!mediaStreamConstraints.video}
      audio={!!mediaStreamConstraints.audio}
      ask={startPreview}
      style={{ aspectRatio: cssAspectRatio }}
    >
      <div
        {...props}
        className={cn(containerStyles(), props.className)}
        style={{ aspectRatio: cssAspectRatio }}
      >
        {mediaStreamConstraints.video ? (
          <>
            {showSpinner && (
              <i
                className={cn('fas fa-circle-notch fa-spin', spinnerStyles())}
              />
            )}
            <video
              ref={mediaRef}
              autoPlay={!mediaBlobUrl}
              className={videoStyles({ playback: !!mediaBlobUrl })}
              style={{ aspectRatio: cssAspectRatio }}
              onClick={toggleMediaPlay}
              playsInline
              preload={'auto'}
            />
            {mediaBlobUrl && (
              <button
                onClick={toggleMediaPlay}
                className={playButtonStyles()}
                disabled={isSubmitting}
              >
                {mediaState === 'playing' ? (
                  <i className="fas fa-stop" />
                ) : (
                  <i className="fas fa-play" />
                )}
              </button>
            )}
          </>
        ) : mediaBlobUrl ? (
          <div className={positionMiddleStyles()}>
            <audio ref={mediaRef} controls />
          </div>
        ) : (
          <AudioIndicatorBar
            mediaStreamTrack={audioTrack}
            className={positionMiddleStyles()}
          />
        )}
        <div className={gradientBGStyles()} />
        <Stack
          direction={'row'}
          css={{
            position: 'absolute',
            width: '100%',
            justifyContent: 'space-between',
            padding: '$4',
          }}
        >
          {mediaBlobUrl ? (
            <Button onClick={clear} disabled={isSubmitting}>
              Cancel
            </Button>
          ) : backButton && !recording ? (
            backButton
          ) : (
            <div />
          )}
          {timer && recording && (
            <>
              <div className={timerContainerStyles()}>
                <Stopwatch timeStart={timer} countdown />
              </div>
              <div />
            </>
          )}
          {!recording && !mediaBlobUrl && (
            <DeviceSelectors disableTriggerStyles className={buttonStyles()} />
          )}
        </Stack>
        <Stack
          direction={'row'}
          gap={2}
          css={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            justifyContent: 'center',
            marginBottom: '$6',
          }}
        >
          {!mediaBlobUrl && (
            <Stack gap={5} css={{ alignItems: 'center' }}>
              {mediaStreamConstraints.video && (
                <AudioIndicatorBar mediaStreamTrack={audioTrack} />
              )}
              <button
                id={RECORD_BTN_ID}
                onClick={toggleRecordingWrapper}
                className={recordButtonStyles()}
              >
                <Stack
                  css={{
                    alignItems: 'center',
                    i: {
                      width: 25,
                      height: 25,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: '#ff9ba7',
                    },
                  }}
                >
                  {recording ? (
                    <i className="fas fa-circle" />
                  ) : mediaStreamConstraints.video ? (
                    <VideoOnIcon />
                  ) : (
                    <MicrophoneOn />
                  )}
                  {recording ? 'Stop' : 'Record'}
                </Stack>
              </button>
              <AppErrorMessage domId={RECORD_BTN_ID} />
            </Stack>
          )}
          {mediaBlobUrl && SubmitButton && (
            <SubmitButton
              onClick={submit}
              disabled={isSubmitDisabled}
              isSubmitting={isSubmitting}
              isUploading={isSubmitDisabled}
            />
          )}
        </Stack>
      </div>
    </DevicePermissionsPrompt>
  )
}

const RECORD_BTN_ID = 'media-record-btn'

const containerStyles = css({
  position: 'relative',
  margin: 'auto',
  height: '100%',
  background: '$grey50',
})

const editorStyles = css({
  'trix-editor': {
    position: 'absolute !important',
    fontSize: '$6 !important',
    top: '15%',
  },
  figure: {
    display: 'none',
  },
})

const videoStyles = css({
  height: '100%',
  position: 'absolute',
  transform: 'scale(-1, 1)',

  variants: {
    playback: {
      true: {
        cursor: 'pointer',
      },
    },
  },
})

const positionMiddleStyles = css({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%,-50%)',
})

const recordButtonStyles = css({
  background: '#ef0a0a',
  color: 'white',
  border: 'none',
  borderRadius: '$4',
  width: 100,
  height: 80,
  cursor: 'pointer',

  svg: {
    fill: 'white',
  },

  '&:focus': {
    borderRadius: '$4',
  },
})

const playButtonStyles = css({
  position: 'absolute !important',
  inset: 0,
  margin: 'auto',
  width: 75,
  height: 75,
  borderRadius: '50%',
  background: 'rgb(255,255,255,0.7)',
  border: 'none',
  cursor: 'pointer',

  i: {
    color: 'black !important',
    fontSize: '$4',
  },

  '&:focus': {
    borderRadius: '50%',
  },
})

const timerContainerStyles = css({
  fontSize: '$3',
  color: 'white',
})

const permissionPromptStyles = css({
  margin: 'auto',
  height: 0,
  flexGrow: 1,
  padding: '0 $5 !important',
})

const spinnerStyles = css({
  position: 'absolute !important',
  inset: 0,
  margin: 'auto',
  width: 75,
  height: 75,
  fontSize: 75,
  opacity: 0.7,
})

const gradientBGStyles = css({
  position: 'absolute',
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  background:
    'linear-gradient(0deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 35%, ' +
    'rgba(0, 0, 0, 0) 70%, rgba(0, 0, 0, 0.5) 100%)',
})
